import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag, Search, ChevronDown } from "lucide-react";
import { useShopify } from "../context/ShopifyContext";
import { useCart } from "../context/CartContext";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isShopDropdownOpen, setIsShopDropdownOpen] = useState(false);
  const [isMobileShopExpanded, setIsMobileShopExpanded] = useState(false);
  const dropdownRef = useRef(null);
  
  const { categories, selectedCategory, setSelectedCategory, loading } = useShopify();
  const { totalQuantity, setIsCartOpen } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsShopDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCategorySelect = (categoryHandle) => {
    setSelectedCategory(categoryHandle);
    setIsShopDropdownOpen(false);
    setIsMobileShopExpanded(false);
    setIsMobileMenuOpen(false);
    // Scroll to shop section
    const shopSection = document.getElementById('shop');
    if (shopSection) {
      shopSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <>
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className={`fixed top-10 left-0 right-0 z-40 transition-all duration-500 ${
          isScrolled
            ? "bg-brand-cream/95 backdrop-blur-xl shadow-sm"
            : "bg-white/95 backdrop-blur-sm shadow-sm"
        }`}
        data-testid="header"
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-12 py-2 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#home"
            className="flex-shrink-0 relative -my-6"
            data-testid="header-logo"
            aria-label="Pure Intentional Home"
          >
            <img 
              src="/images/logo.png" 
              alt="Pure Intentional - Tropical Hair Care" 
              className="h-28 md:h-32 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-10">
            <a
              href="#home"
              className="font-body text-sm tracking-wider text-brand-coconutBrown/80 hover:text-brand-deepGreen transition-colors duration-300 link-underline"
              data-testid="nav-link-home"
            >
              Home
            </a>

            {/* Shop Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsShopDropdownOpen(!isShopDropdownOpen)}
                onMouseEnter={() => setIsShopDropdownOpen(true)}
                className="flex items-center gap-1 font-body text-sm tracking-wider text-brand-coconutBrown/80 hover:text-brand-deepGreen transition-colors duration-300"
                data-testid="nav-link-shop"
              >
                Shop
                <ChevronDown 
                  className={`w-4 h-4 transition-transform duration-200 ${isShopDropdownOpen ? 'rotate-180' : ''}`} 
                  strokeWidth={1.5} 
                />
              </button>

              <AnimatePresence>
                {isShopDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    onMouseLeave={() => setIsShopDropdownOpen(false)}
                    className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-brand-softBeige overflow-hidden"
                  >
                    <div className="py-2">
                      {/* All Products */}
                      <button
                        onClick={() => handleCategorySelect('all')}
                        className={`w-full text-left px-4 py-3 font-body text-sm tracking-wider transition-colors duration-200 ${
                          selectedCategory === 'all'
                            ? 'bg-brand-softBeige text-brand-deepGreen font-medium'
                            : 'text-brand-coconutBrown/80 hover:bg-brand-softBeige/50 hover:text-brand-deepGreen'
                        }`}
                      >
                        All Products
                      </button>

                      {/* Divider */}
                      {categories.length > 0 && (
                        <div className="border-t border-brand-softBeige my-1" />
                      )}

                      {/* Categories from Shopify Collections */}
                      {loading ? (
                        <div className="px-4 py-3 text-brand-coconutBrown/50 text-sm">
                          Loading categories...
                        </div>
                      ) : categories.length > 0 ? (
                        categories.map((category) => (
                          <button
                            key={category.id}
                            onClick={() => handleCategorySelect(category.handle)}
                            className={`w-full text-left px-4 py-3 font-body text-sm tracking-wider transition-colors duration-200 ${
                              selectedCategory === category.handle
                                ? 'bg-brand-softBeige text-brand-deepGreen font-medium'
                                : 'text-brand-coconutBrown/80 hover:bg-brand-softBeige/50 hover:text-brand-deepGreen'
                            }`}
                          >
                            {category.title}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-brand-coconutBrown/50 text-sm">
                          No categories found
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {navLinks.slice(1).map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="font-body text-sm tracking-wider text-brand-coconutBrown/80 hover:text-brand-deepGreen transition-colors duration-300 link-underline"
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* Desktop Icons */}
          <div className="hidden md:flex items-center gap-6">
            <button
              className="text-brand-coconutBrown/80 hover:text-brand-deepGreen transition-colors duration-300"
              aria-label="Search"
              data-testid="search-btn"
            >
              <Search className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <button
              onClick={() => setIsCartOpen(true)}
              className="text-brand-coconutBrown/80 hover:text-brand-deepGreen transition-colors duration-300 relative"
              aria-label="Shopping bag"
              data-testid="cart-btn"
            >
              <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-warmGold text-white text-[10px] rounded-full flex items-center justify-center">
                {totalQuantity}
              </span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-brand-deepGreen"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" strokeWidth={1.5} />
            ) : (
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            )}
          </button>
        </nav>
      </motion.header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-0 z-50 bg-brand-cream md:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col min-h-full">
              <div className="flex items-center justify-between px-6 py-4 border-b border-brand-softBeige">
                <img 
                  src="/images/logo.png" 
                  alt="Pure Intentional" 
                  className="h-16 w-auto object-contain"
                />
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                >
                  <X className="w-6 h-6 text-brand-deepGreen" strokeWidth={1.5} />
                </button>
              </div>
              
              <nav className="flex flex-col py-8 px-6">
                {/* Home */}
                <a
                  href="#home"
                  className="font-heading text-2xl font-light text-brand-deepGreen hover:text-brand-warmGold transition-colors py-4 border-b border-brand-softBeige"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </a>

                {/* Shop with expandable categories */}
                <div className="border-b border-brand-softBeige">
                  <button
                    onClick={() => setIsMobileShopExpanded(!isMobileShopExpanded)}
                    className="w-full flex items-center justify-between font-heading text-2xl font-light text-brand-deepGreen hover:text-brand-warmGold transition-colors py-4"
                  >
                    Shop
                    <ChevronDown 
                      className={`w-5 h-5 transition-transform duration-200 ${isMobileShopExpanded ? 'rotate-180' : ''}`} 
                      strokeWidth={1.5} 
                    />
                  </button>
                  
                  <AnimatePresence>
                    {isMobileShopExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="pl-4 pb-4 space-y-2">
                          {/* All Products */}
                          <button
                            onClick={() => handleCategorySelect('all')}
                            className={`block w-full text-left font-body text-lg py-2 transition-colors ${
                              selectedCategory === 'all'
                                ? 'text-brand-warmGold font-medium'
                                : 'text-brand-coconutBrown/70 hover:text-brand-deepGreen'
                            }`}
                          >
                            All Products
                          </button>

                          {/* Categories */}
                          {categories.map((category) => (
                            <button
                              key={category.id}
                              onClick={() => handleCategorySelect(category.handle)}
                              className={`block w-full text-left font-body text-lg py-2 transition-colors ${
                                selectedCategory === category.handle
                                  ? 'text-brand-warmGold font-medium'
                                  : 'text-brand-coconutBrown/70 hover:text-brand-deepGreen'
                              }`}
                            >
                              {category.title}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Other Links */}
                {navLinks.slice(1).map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    className="font-heading text-2xl font-light text-brand-deepGreen hover:text-brand-warmGold transition-colors py-4 border-b border-brand-softBeige"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.name}
                  </a>
                ))}
              </nav>

              <div className="flex items-center justify-center gap-8 py-8 border-t border-brand-softBeige mt-auto">
                <button aria-label="Search" data-testid="mobile-search-btn">
                  <Search className="w-6 h-6 text-brand-deepGreen" strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setIsCartOpen(true);
                  }}
                  aria-label="Shopping bag"
                  data-testid="mobile-cart-btn"
                  className="relative"
                >
                  <ShoppingBag className="w-6 h-6 text-brand-deepGreen" strokeWidth={1.5} />
                  {totalQuantity > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-warmGold text-white text-[10px] rounded-full flex items-center justify-center">
                      {totalQuantity}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
